/**
 * V3ListingRow — one live listing as a dense ledger row. THE LOOK (Matt
 * 2026-08-26) puts search on the Ledger register: 13px working text, Geist
 * Mono tabular figures, photography as a small thumb, hairline separations,
 * radius 0, no elevation shadow. This is the barrel's listing unit for those
 * surfaces; the flat-register ListingCard stays what it is for the pages that
 * still wear other languages, and this component takes the same data shape so
 * a consumer swaps an import, not a mapper.
 *
 * Not a seventh pattern: a composition unit the search surfaces stack inside
 * their own list/rail chrome, the way V3PlaceDocuments composes Ledger.
 *
 * Barrel law honored here:
 *  - No import from the deleted KB register, components/site (flat),
 *    components/site/primitives, components/site/explore, or components/ui.
 *    Only ./atoms, ./tokens.css, and lib helpers.
 *  - No raw color: every visual value reaches this row through var(--v3-*).
 *  - The register is read, never branched on: under `.v3--ledger` the tokens
 *    make this row dense and mono; the component carries V3_ROOT_CLASS itself
 *    so the token scope always resolves.
 *
 * ONE deliberate departure from the "figures arrive preformatted" data rule,
 * stated here so it is a decision and not a drift: the ask and the price per
 * square foot are published by the lib publishers (formatPublishedSaleAsk,
 * publishListingShareKind, publishListingSharePricePerSqft) INSIDE this
 * component, exactly as ListingCard does and for the reason its header
 * documents at length — a caller that can omit the fractional-share label or
 * the commercial-lease guard is a caller that will, and both defects shipped
 * before the guards moved into the card. The section 0 guard outranks the
 * convention. The AST gate (check-public-v3.mjs rule 3) polices formatting
 * calls in this file itself; the publishers live in lib and stay the one
 * definition of what an ask means.
 */
import type { ReactNode } from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatPublishedSaleAsk } from '@/lib/listing/publish-listing-ask'
import { publishListingLeaseFigure } from '@/lib/listing/publish-lease-rate'
import { SparkSafeImage } from '@/lib/listing/SparkSafeImage'
import { listingRowPhotoSrc } from '@/lib/listing/row-photo'
import {
  publishListingShareKind,
  publishListingSharePricePerSqft,
} from '@/lib/listing/publish-listing-share'
import { V3_ROOT_CLASS } from './atoms'
import { listingPhotoAlt } from './listing-photo-alt'
import { V3PhotoFallback } from './V3PhotoFallback'
import './tokens.css'
import './V3ListingRow.css'

export type V3ListingRowBadge = 'hot' | 'new' | 'drop' | 'open' | 'sold' | 'pending' | 'video'

/**
 * Structurally identical to the flat register's ListingCardData ON PURPOSE
 * (not imported — the barrel is standalone): every mapper that feeds
 * ListingCard feeds this row unchanged, so the search surfaces restyle by
 * swapping an import. The required-not-optional fields carry the same
 * section 0 rationale as the original: `propertyType`, `propertySubType`,
 * `subdivisionName`, `city`, and `listNumber` exist so the ask and the $/sqft
 * publishers always know what kind of listing they are drawing. `null` is a
 * legitimate value; leaving the key out is not.
 */
export type V3ListingRowData = {
  listingKey: string
  href: string
  photoUrl?: string | null
  price: number | null
  /** Top line of address (e.g. "2732 NW Ordway Ave"). */
  addressLine: string
  /** City/state/zip + neighborhood (e.g. "Bend, OR 97703 · Awbrey Butte"). */
  cityLine: string
  beds: number | null
  baths: number | null
  sqft: number | null
  /** Whole-dollar price per living sqft; rendered only with `showPricePerSqft`. */
  pricePerSqft?: number | null
  propertyType: string | null
  propertySubType: string | null
  subdivisionName: string | null
  city: string | null
  listNumber: string | null
  tourUrl?: string | null
  hasTour?: boolean
  /**
   * The card's tour control, in the listing page's words ("Video Tour" for a
   * walkthrough reel, "3D" for a 3D tour). Omit it and the card reads it off
   * `tourUrl` (publishListingTourLabel), so a walkthrough is never called a 3D tour.
   */
  tourLabel?: string | null
  badge?: { kind: V3ListingRowBadge; label: string }
  /** Overlay pills. When set, this is the whole set; `badge` is ignored. */
  badges?: Array<{ kind: V3ListingRowBadge; label: string }>
  /** "Pending" for an under-contract listing. Printed by the rail card, not the row. */
  statusLabel?: string | null
  /**
   * The MLS StandardStatus the row was read with. A place page's counts read it
   * through publicCountState (lib/listing-status-public), the same classifier
   * the place map draws its marks with, so "for sale" means one thing on the
   * map and under it (SITE-193).
   */
  standardStatus?: string | null
  /**
   * A commercial lease's rent unit, the feed's own "Lease Rate Options" value
   * ("$/SF/Mo", "$ Amt/Mo", ...), read by getLeaseRateOptions. Only lease rows
   * carry it. Every card hands it to publishListingLeaseFigure with `price`, so
   * a lease prints its rate with the unit, or "Lease rate not published", where
   * a sale listing prints its ask.
   */
  leaseRateOption?: string | null
  /**
   * A commercial lease's terms as the listing files them, already worded by
   * publishLeaseTerms ("NNN lease", "Tenant pays taxes, insurance and
   * utilities", "Zoned IL", "15 parking spaces"). Only lease rows carry it; the
   * dial's card prints each one under the rent. Absent or empty prints nothing.
   */
  leaseTerms?: readonly string[] | null
  /**
   * SITE-194: whether the listing has a walkthrough reel a card can play.
   * An optional hint from data the page already holds; only an explicit
   * false stops the dial asking /api/listings/[key]/card-video after the
   * dwell. Undefined means "ask".
   */
  hasVideo?: boolean | null
}

/** Badge kinds that print solid navy; the rest are hairline outline tags. */
const SOLID_BADGE: Record<V3ListingRowBadge, boolean> = {
  hot: true,
  drop: true,
  sold: true,
  pending: true,
  new: false,
  open: true,
  video: false,
}

/** beds · baths · sqft · $/sqft — only the values the listing actually has.
 *  No placeholder dashes for land/estate parcels with no living-area stats. */
export function metaParts(listing: V3ListingRowData, showPricePerSqft: boolean): string[] {
  const parts: string[] = []
  if (listing.beds != null && listing.beds > 0) parts.push(`${Math.round(listing.beds).toLocaleString('en-US')} bd`)
  if (listing.baths != null && listing.baths > 0) parts.push(`${Math.round(listing.baths).toLocaleString('en-US')} ba`)
  if (listing.sqft != null && listing.sqft > 0) parts.push(`${Math.round(listing.sqft).toLocaleString('en-US')} sqft`)
  if (showPricePerSqft) {
    const published = publishListingSharePricePerSqft({
      propertyType: listing.propertyType,
      propertySubType: listing.propertySubType,
      subdivisionName: listing.subdivisionName,
      city: listing.city,
      listNumber: listing.listNumber,
      pricePerSqft: listing.pricePerSqft ?? null,
    })
    if (published != null && published > 0) {
      parts.push(`$${Math.round(published).toLocaleString('en-US')}/sqft`)
    }
  }
  return parts
}

export function V3ListingRow({
  listing,
  showPricePerSqft = false,
  priority = false,
  className,
  onOpenTour,
  compare,
}: {
  listing: V3ListingRowData
  /** Opt-in: append "$X/sqft" to the figure column (search results). */
  showPricePerSqft?: boolean
  /** LCP: pass for the first few above-the-fold rows. */
  priority?: boolean
  /** Extra row classes — `is-hot` / `is-active` for the split view's map sync. */
  className?: string
  /** 3D badge opens the on-site tour viewer. */
  onOpenTour?: () => void
  /** SITE-110: labeled comparison against the visible set. */
  compare?: ReactNode
}) {
  // A row may not show a fractional ask unlabelled (decided 2026-08-19): the
  // share label is computed HERE from the subject the row already carries,
  // never passed in beside `badge`. See the header.
  const shareKind = publishListingShareKind({
    propertySubType: listing.propertySubType,
    subdivisionName: listing.subdivisionName,
    city: listing.city,
    listNumber: listing.listNumber,
  })
  // Sale-aware ask: on MLS PropertyType 'G' (Commercial Lease) ListPrice is
  // rent per square foot, and the publisher declines to print it as an ask.
  const ask = formatPublishedSaleAsk({
    price: listing.price,
    propertyType: listing.propertyType,
  })
  // The lease half of the same guard: that rent prints with its unit, or
  // "Lease rate not published", beside "For lease" (never a bare number).
  const lease = publishListingLeaseFigure({
    price: listing.price,
    propertyType: listing.propertyType,
    leaseRateOption: listing.leaseRateOption ?? null,
  })
  const figure = lease ? lease.text : (ask ?? '—')
  const tagText = lease ? lease.label : shareKind
  // A lease's terms as its listing files them (publishLeaseTerms' words), under
  // the street, so a row does not drop what the dial's card prints (2026-09-30).
  const leaseTerms = lease
    ? (listing.leaseTerms ?? []).map((term) => term.trim()).filter(Boolean).join(' · ')
    : ''
  const meta = metaParts(listing, showPricePerSqft)
  const splitThumb = typeof className === 'string' && className.includes('v3-lrow--split')
  const tags = listing.badges ?? (listing.badge ? [listing.badge] : [])
  const photoTags = tags.filter((tag) => tag.kind !== 'video')
  const hasTour = Boolean(listing.tourUrl) || listing.hasTour === true

  const still = (
    <>
      {listing.photoUrl ? (
        <SparkSafeImage
          src={listingRowPhotoSrc(listing.photoUrl)}
          alt={listingPhotoAlt(listing)}
          fill
          priority={priority}
          loading={priority ? 'eager' : 'lazy'}
          sizes={splitThumb ? '200px' : '72px'}
        />
      ) : (
        <V3PhotoFallback name={listing.city ?? listing.addressLine} />
      )}
      {photoTags.length > 0 ? (
        <span className="v3-lrow__photo-tags">
          {photoTags.map((tag) => (
            <span
              key={`${tag.kind}-${tag.label}`}
              className={cn('v3-lrow__tag', SOLID_BADGE[tag.kind] && 'v3-lrow__tag--solid')}
            >
              {tag.label}
            </span>
          ))}
        </span>
      ) : null}
      <span className="v3-lrow__addr-tip">{listing.addressLine}</span>
    </>
  )

  const tourControl = hasTour ? (
    onOpenTour ? (
      <button
        type="button"
        className="v3-lrow__tour"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onOpenTour()
        }}
      >
        <span className="v3-lrow__tour-label">3D tour</span>
      </button>
    ) : (
      <span className="v3-lrow__tour">
        <span className="v3-lrow__tour-label">3D tour</span>
      </span>
    )
  ) : null

  if (splitThumb) {
    // SITE-115: photo and copy share the detail href. Tour stays outside the
    // photo link so a button is never nested inside an anchor.
    return (
      <article className={cn(V3_ROOT_CLASS, 'v3-lrow', className)}>
        <div className="v3-lrow__media">
          <Link
            href={listing.href}
            className="v3-lrow__photo-link"
            aria-label={`Open ${listing.addressLine}`}
          >
            {still}
          </Link>
          {tourControl}
        </div>
        <Link href={listing.href} className="v3-lrow__copy">
          <span className="v3-lrow__price">{figure}</span>
          {tagText ? <span className="v3-lrow__tag">{tagText}</span> : null}
          {meta.length > 0 ? <span className="v3-lrow__meta">{meta.join(' · ')}</span> : null}
          {compare}
          <span className="v3-lrow__addr">{listing.addressLine}</span>
          <span className="v3-lrow__city">{listing.cityLine}</span>
          {leaseTerms ? <span className="v3-lrow__terms">{leaseTerms}</span> : null}
        </Link>
      </article>
    )
  }

  return (
    <Link href={listing.href} className={cn(V3_ROOT_CLASS, 'v3-lrow', className)}>
      <span className="v3-lrow__media" aria-hidden>
        {still}
        {tourControl}
      </span>
      <span className="v3-lrow__body">
        <span className="v3-lrow__addr">{listing.addressLine}</span>
        <span className="v3-lrow__city">{listing.cityLine}</span>
        {leaseTerms ? <span className="v3-lrow__terms">{leaseTerms}</span> : null}
      </span>
      <span className="v3-lrow__figures">
        <span className="v3-lrow__price">{figure}</span>
        {tagText ? <span className="v3-lrow__tag">{tagText}</span> : null}
        {meta.length > 0 ? <span className="v3-lrow__meta">{meta.join(' · ')}</span> : null}
        {compare}
      </span>
    </Link>
  )
}
